import {
  GA_MEASUREMENT_ID,
  GA_REQUIRE_CONSENT,
  GA_SHELTER_CONTEXT_STORAGE_KEY,
} from './constants';
import { isAnalyticsConsentGranted } from './consent';
import type { Ga4EventName, Ga4EventParams } from './types';

let inMemoryShelterId: string | undefined;

export function isGaConfigured(): boolean {
  return GA_MEASUREMENT_ID.length > 0;
}

export function canSendAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isGaConfigured()) return false;
  if (!GA_REQUIRE_CONSENT) return true;
  return isAnalyticsConsentGranted();
}

export function mergeGaEventParams(
  params: Ga4EventParams | undefined,
  shelterId?: string,
): Ga4EventParams {
  const resolvedShelterId =
    params?.shelter_id?.trim() ||
    shelterId?.trim() ||
    inMemoryShelterId?.trim() ||
    readPersistedShelterId();

  if (!resolvedShelterId) {
    return params ?? {};
  }

  return {
    ...params,
    shelter_id: resolvedShelterId,
  };
}

function readPersistedShelterId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const value = sessionStorage.getItem(GA_SHELTER_CONTEXT_STORAGE_KEY);
    return value?.trim() || undefined;
  } catch {
    return undefined;
  }
}

/** Sets tenant context merged into subsequent `trackGaEvent` calls (Phase 1+). */
export function setShelterAnalyticsContext(shelterId: string | null | undefined): void {
  const trimmed = shelterId?.trim();
  inMemoryShelterId = trimmed || undefined;

  if (typeof window === 'undefined') return;
  try {
    if (trimmed) {
      sessionStorage.setItem(GA_SHELTER_CONTEXT_STORAGE_KEY, trimmed);
    } else {
      sessionStorage.removeItem(GA_SHELTER_CONTEXT_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

export function clearShelterAnalyticsContext(): void {
  setShelterAnalyticsContext(undefined);
}

export function updateGaConsent(granted: boolean): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'denied' : 'denied',
  });
}

export function trackGaEvent(
  eventName: Ga4EventName,
  params?: Ga4EventParams,
): void {
  if (!canSendAnalytics()) return;
  if (!window.gtag) return;

  window.gtag('event', eventName, mergeGaEventParams(params, inMemoryShelterId));
}

export function trackGaPageView(pagePath: string): void {
  trackGaEvent('page_view', { page_path: pagePath });
}
